import Sentry, { featureTags } from '@/services/sentry'
import { findUserPreferenceDefinition } from '@/business-logic/user-preferences/userPreferenceValues'

const HOOK_PATH = /^\.\/([^/]+)\/ext\/([^/]+)\.js$/

// Which hooks each part of a component takes: a computed has no arguments to change, a render only its result.
// `instead` replaces a method and gets `original` to call through when the setting leaves the default behaviour.
const ALLOWED_HOOKS = Object.freeze({ methods: ['before', 'instead', 'after'], computed: ['after'], render: ['after'] })

export const reportSettingHookProblem = (message) => {
  // eslint-disable-next-line no-console
  console.warn(message)
  Sentry.captureMessage(message, { level: 'warning', tags: featureTags({ featureName: 'setting-hooks', taskId: 'PRRW-313' }) })
}

const isPlainObject = (value) => Boolean(value) && Object.getPrototypeOf(value) === Object.prototype

const findHandlerProblem = (handlers, allowed, where) => {
  if (!isPlainObject(handlers)) return `${where} must be an object of ${allowed.join('/')} functions`
  const names = Object.keys(handlers)
  if (!names.length) return `${where} has no ${allowed.join('/')} function`
  const wrong = names.find((name) => !allowed.includes(name) || typeof handlers[name] !== 'function')
  return wrong ? `${where}.${wrong} must be one of ${allowed.join('/')} and a function` : null
}

const findNamedHandlersProblem = (spec, part) => {
  if (!spec[part]) return null
  if (!isPlainObject(spec[part])) return `${part} must be an object keyed by ${part} name`
  return Object.entries(spec[part]).reduce(
    (problem, [name, handlers]) => problem || findHandlerProblem(handlers, ALLOWED_HOOKS[part], `${part}.${name}`),
    null,
  )
}

const isNameList = (list) => Array.isArray(list) && list.every((item) => typeof item === 'string' && item)

// A render hook names the template refs and child components it relies on, so a template edit that drops one is
// caught by the settings-folder unit test, not silently missed.
const findRenderProblem = (render) => {
  if (!isPlainObject(render)) return 'render must be an object with refs or components, and after'
  const { refs = [], components = [], ...handlers } = render
  const valid = isNameList(refs) && isNameList(components) && refs.length + components.length > 0
  if (!valid) return 'render.refs or render.components must list the template refs or child components the hook relies on'
  return findHandlerProblem(handlers, ALLOWED_HOOKS.render, 'render')
}

const findShapeProblem = (spec) => {
  if (!isPlainObject(spec)) return 'it must export an object with methods, computed or render'
  const parts = Object.keys(spec)
  if (!parts.length) return 'it hooks nothing'
  const unknown = parts.find((part) => !ALLOWED_HOOKS[part])
  if (unknown) return `"${unknown}" is not methods, computed or render`
  return (spec.render && findRenderProblem(spec.render))
    || findNamedHandlersProblem(spec, 'methods')
    || findNamedHandlersProblem(spec, 'computed')
}

/**
 * The methods of one component that more than one setting replaces, as `[{ name, settings: [keys] }]`. `instead` hooks
 * chain: the last setting in key order runs first, and when it does not call `original` the others never run, so one
 * setting would silently switch another off. `before` and `after` hooks all run, so they never clash.
 */
export const findInsteadClashes = (entries) => {
  const replacedBy = {}
  entries.forEach(({ setting, spec }) => Object.entries(spec.methods || {}).forEach(([name, handlers]) => {
    if (handlers.instead) replacedBy[name] = [...(replacedBy[name] || []), setting.key]
  }))
  return Object.entries(replacedBy)
    .filter(([, settings]) => settings.length > 1)
    .map(([name, settings]) => ({ name, settings }))
}

const skipped = (path) => `Skipped setting hook "${path.replace(/^\.\//, '')}"`

/**
 * Lists the hook files under the settings folder, `./<setting key>/ext/<component name>.js`, with the setting each
 * belongs to, without loading them. A file whose folder is not a registered setting is skipped with a warning.
 */
export const indexSettingHookFiles = (paths, registry) => [...paths].sort().reduce((files, path) => {
  const [, key, componentName] = HOOK_PATH.exec(path) || []
  const setting = key && findUserPreferenceDefinition(registry, key)
  if (!setting) {
    reportSettingHookProblem(`${skipped(path)}: its folder is not a registered setting`)
    return files
  }
  return [...files, Object.freeze({ path, componentName, setting })]
}, [])

// The hook spec a loaded hook file exports, or null (with a warning) when it is not a valid one.
export const readHookSpec = (path, loaded) => {
  const spec = loaded && loaded.__esModule ? loaded.default : loaded
  const problem = findShapeProblem(spec)
  if (!problem) return spec
  reportSettingHookProblem(`${skipped(path)}: ${problem}`)
  return null
}

export const reportHookLoadFailure = (path, error) => {
  reportSettingHookProblem(`${skipped(path)}: it failed to load (${error.message})`)
}

/**
 * Builds `{ [component name]: [{ setting, spec }] }` by loading every hook file of a synchronous webpack
 * `require.context` over the settings folder. The app loads hook files lazily instead (see index.js); this is for
 * tests and tools that need every hook at once. A file that fails to load or is not a valid spec is skipped.
 */
export const buildSettingHooks = (hooksContext, registry) => {
  const hooks = {}
  indexSettingHookFiles(hooksContext.keys(), registry).forEach(({ path, componentName, setting }) => {
    let loaded
    try {
      loaded = hooksContext(path)
    } catch (error) {
      reportHookLoadFailure(path, error)
      return
    }
    const spec = readHookSpec(path, loaded)
    if (spec) hooks[componentName] = [...(hooks[componentName] || []), Object.freeze({ setting, spec })]
  })
  return Object.freeze(hooks)
}
